import React, { useState } from "react";
import { IProject, IBook } from "../../services/ttsService";

interface Props {
  projects: IProject[];
  loading: boolean;
  isAdmin: boolean;
  partnerNames: Record<string, string>;
  booksByProject: Record<string, IBook[]>;
  loadingBooks: Record<string, boolean>;
  onExpandProject: (projectId: string) => void;
  onOpenBook: (project: IProject, book: IBook) => void;
  onNewBook: (project: IProject) => void;
  onNewProject: () => void;
  onAssignPartner: (project: IProject) => void;
  onDeleteProject: (projectId: string, projectName: string) => void;
  onDeleteBook: (bookId: string, projectId: string, bookTitle: string) => void;
  onEditProject: (project: IProject) => void;
  clipCountsByBook: Record<string, { total: number; chapters: Record<string, number> }>;
  mergedCountsByBook: Record<string, number>;
}
const truncateTitle = (projectName: string) => projectName.length > 20 ? projectName.slice(0, 20) + "…" : projectName;


const ProjectsView: React.FC<Props> = ({
  projects, loading, isAdmin, partnerNames, booksByProject, loadingBooks,
  onExpandProject, onOpenBook, onNewBook,
  onNewProject, onAssignPartner, onDeleteProject, onDeleteBook, onEditProject,
  clipCountsByBook, mergedCountsByBook,
}) => {
  const [expanded, setExpanded] = useState<string | null>(null);
  const toggle = (id: string) => {
    const next = expanded === id ? null : id;
    setExpanded(next);
    if (next) onExpandProject(id);
  };

  return (
    <div className="pv-wrap">
      <div className="pv-header">
        <div>
          <div className="pv-title">{isAdmin ? "All Projects" : "My Projects"}</div>
          <div className="pv-sub">{projects.length} workspace{projects.length !== 1 ? "s" : ""}</div>
        </div>
        {isAdmin && (
          <button className="pv-btn-new" onClick={onNewProject}>＋ New Project</button>
        )}
      </div>

      {loading && <span className="cp-spinner">Loading projects…</span>}
      {!loading && !projects.length && (
        <div className="pv-empty">
          {isAdmin
            ? "No projects yet. Create your first workspace!"
            : "No projects assigned to you yet."}
        </div>
      )}
      

      {projects.map(p => {
        const isOpen = expanded === p._id;
        const books = booksByProject[p._id] || [];
        const bookCount = booksByProject[p._id]?.length ?? p.bookCount ?? 0;
        const busy = loadingBooks[p._id];
        const assignedId = p.partnerId ?? p.publisherId ?? "";
        const assignedName = assignedId ? partnerNames[assignedId] : null;

        

        return (
          <div key={p._id} className="pv-project">  
            {/* Project row */}
            <div className="pv-project__row">
              <div className="pv-project__icon">🗂</div>
              <div className="pv-project__info">
                <div className="pv-project__name">{p.projectName}</div>
                <div className="pv-project__meta">
                  <span title={p.description.length > 20 ? p.description : undefined}>
                    {truncateTitle(p.description) || "No description"}
                  </span>
                  {p.editor && <span>{" · "}{p.editor}</span>}
                  {isAdmin && (
                    <span className="pv-project__count">
                      {" · "}Partner: {assignedName || "Unassigned"}
                    </span>
                  )}
                  {bookCount > 0 && (
                    <span className="pv-project__count">
                      {" · "}{bookCount} book{bookCount !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              </div>
              <div className="pv-project__actions">
                <button className="pv-btn-expand" onClick={() => toggle(p._id)}>
                  {isOpen ? "▲ Hide" : "▼ Books"}
                </button>
                {isAdmin && (
                  <button className="pv-btn-add" onClick={() => onNewBook(p)}>＋ Book</button>
                )}
                {isAdmin && (
                  <button className="pv-btn-expand" onClick={() => onAssignPartner(p)}>
                    {`${assignedName && assignedName !== null ? assignedName : "Assign Partner"}`}
                  </button>
                )}
                {isAdmin && (
                  <>
                  
                  <button
                    className="pv-btn-del"
                    title="Delete project"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteProject(p._id, p.projectName);
                    }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                      <path d="M10 11v6M14 11v6" />
                      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                    </svg>
                  </button>
                  <button
                    className="pv-btn-edit"
                    title="Edit project"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditProject(p);
                    }}
                  >
                   <svg
  width="13"
  height="13"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  strokeWidth="2"
  strokeLinecap="round"
  strokeLinejoin="round"
>
  <path d="M12 20h9" />
  <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
</svg>
                  </button>
                  </>
                )}
              </div>
            </div>

            {/* Books accordion */}
            {isOpen && (
              <div className="pv-books">
                {busy && <span className="cp-spinner" style={{ margin: "10px 0 10px 16px" }}>Loading books…</span>}

                {!busy && !books.length && (
                  <div className="pv-books__empty">
                    No books yet.
                    {isAdmin && (
                      <button className="pv-btn-inline" onClick={() => onNewBook(p)}>＋ Add first book</button>
                    )}
                  </div>
                )}

                {books.map(b => {
                  const stats = clipCountsByBook[b._id];
                  const mergedCount = mergedCountsByBook[b._id] ?? 0;
                  const chEntries = stats
                    ? Object.entries(stats.chapters).sort(([a], [b]) => a.localeCompare(b))
                    : [];

                  return (
                    <div key={b._id} className="pv-book">
                      <div className="pv-book__header" onClick={() => onOpenBook(p, b)}>
                        <div className="pv-book__icon">📖</div>
                        <div className="pv-book__info">
                          <div className="pv-book__title">{b.title}</div>
                          <div className="pv-book__metarow">
                            <div className="pv-book__meta">
                              {b.language}{b.model ? ` · ${b.model}` : ""}
                              {stats && (
                                <span className="pv-book__stats">
                                  {" · "}{stats.total} clip{stats.total !== 1 ? "s" : ""}
                                  {" · "}{chEntries.length} chapter{chEntries.length !== 1 ? "s" : ""}
                                  {mergedCount > 0 && ` · ${mergedCount} merged`}
                                </span>
                              )}
                            </div>
                            <span className="pv-book__open">Open →</span>
                          </div>
                        </div>
                      </div>

                      {chEntries.length > 0 && (
                        <div className="pv-chapters">
                          {chEntries.map(([chapter, count]) => (
                            <div key={chapter} className="pv-chapter-row">
                              <span className="pv-chapter-row__name">▸ {chapter}</span>
                              <span className="pv-chapter-row__count"> {count} clip{count !== 1 ? "s" : ""}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <button 
                        className="pv-btn-del" 
                        title="Delete book"
                        onClick={(e) => {
                          e.stopPropagation();   
                          onDeleteBook(b._id, p._id, b.title);
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6M14 11v6" />
                          <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                        </svg>
                      </button>

                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ProjectsView;